/**
 * Weekly review tab: KPI grid (WoW / vs 4 weeks / YoY), target pace, revenue bridge, by platform / brand /
 * category with sparklines, Amazon Vendor PO block, movers with cause tags, suggested actions.
 * Props: data (GET /api/review/wbr), showCosts (admin), onScope({brand|category|platform}) to filter the page.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkline } from '../sales/Sparkline';
import { MetricToggle } from '../sales/views/common';
import { shortMoney } from '../sales/format';
import { ActionList, BridgeChart, Delta, Empty, Metric, Money, Notes, Num, Pct, Section, TagChip } from './ReviewPieces';

const DIMENSIONS = [['by_platform', 'Platforms'], ['by_brand', 'Brands'], ['by_category', 'Categories']];

const DimensionTable = ({ rows, dim, showCosts, onScope }) => {
  if (!rows?.length) return <Empty>No sales in this week.</Empty>;
  const scopeKey = dim === 'by_platform' ? 'platform' : dim === 'by_brand' ? 'brand' : 'category';
  return (
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[720px] text-sm" data-testid={`review-${dim}`}>
        <thead className="bg-gray-50 text-xs text-gray-600">
          <tr>
            <th scope="col" className="px-3 py-2 text-left font-medium">Name</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">Revenue</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">WoW</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">vs 4 wk</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">YoY</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">Units</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">ASP</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">Discount</th>
            <th scope="col" className="px-3 py-2 text-right font-medium">SKUs</th>
            {showCosts && <th scope="col" className="px-3 py-2 text-right font-medium">Contribution</th>}
            <th scope="col" className="w-28 px-3 py-2 text-left font-medium">13 weeks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.key} className="border-t border-gray-100">
              <td className="px-3 py-1.5">
                <button type="button" onClick={() => onScope({ [scopeKey]: row.key })} className="font-medium text-gray-900 hover:text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label={`Filter the review to ${row.label}`}>
                  {row.label}
                </button>
              </td>
              <td className="px-3 py-1.5 text-right tabular-nums"><Money v={row.revenue?.value} /></td>
              <td className="px-3 py-1.5 text-right"><Delta pct={row.revenue?.wow_pct} /></td>
              <td className="px-3 py-1.5 text-right"><Delta pct={row.revenue?.vs4wk_pct} /></td>
              <td className="px-3 py-1.5 text-right"><Delta pct={row.revenue?.yoy_pct} /></td>
              <td className="px-3 py-1.5 text-right tabular-nums"><Num v={row.units?.value} /></td>
              <td className="px-3 py-1.5 text-right tabular-nums"><Money v={row.asp?.value} /></td>
              <td className="px-3 py-1.5 text-right tabular-nums"><Pct v={row.discount_pct?.value} /></td>
              <td className="px-3 py-1.5 text-right tabular-nums"><Num v={row.skus_sold?.value} /></td>
              {showCosts && <td className="px-3 py-1.5 text-right tabular-nums"><Money v={row.contribution?.value} /></td>}
              <td className="px-3 py-1.5"><Sparkline data={row.sparkline} dataKey="revenue" height={22} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const TargetBlock = ({ target }) => {
  if (!target) return null;
  if (!target.available) return <Empty>{target.reason || 'No targets for this month.'}</Empty>;
  const platforms = Object.entries(target.platforms || {});
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label={`Month target ${target.month}`} entry={{ value: target.month_target }} />
        <Metric label="Month to date" entry={{ value: target.mtd_actual }} footnote={`${target.attainment_pct}% of the month target · pace ${target.pace_pct ?? '—'}% of the prorated target`} />
        <Metric label="Needed per day" entry={{ value: target.required_daily_run_rate }} footnote={`${target.days_left} days left · recent run-rate ${shortMoney(target.recent_daily_run_rate)}/day`} />
        <Metric label="Projected month end" entry={{ value: target.projected_month_end }} footnote={target.projected_month_end >= target.month_target ? 'On track at the recent run-rate' : `Short by ${shortMoney(target.month_target - target.projected_month_end)} at the recent run-rate`} />
      </div>
      {platforms.length > 0 && (
        <ul className="flex flex-wrap gap-2 text-xs text-gray-700">
          {platforms.map(([code, row]) => (
            <li key={code} className="rounded-full bg-gray-100 px-2.5 py-1">
              {row.label}: {shortMoney(row.actual)} of {shortMoney(row.month_target)} ({row.attainment_pct ?? '—'}%)
            </li>
          ))}
        </ul>
      )}
      <p className="text-[11px] text-gray-500">{target.basis}</p>
    </div>
  );
};

const VendorBlock = ({ vendor }) => {
  if (!vendor) return null;
  if (!vendor.available) return <Empty>{vendor.reason}</Empty>;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Metric label="POs" entry={{ value: vendor.pos }} kind="number" footnote={`${shortMoney(vendor.requested_value)} requested`} />
      <Metric label="Confirmation rate" entry={{ value: vendor.confirmation_rate_pct }} kind="percent" footnote={`${vendor.accepted_units} of ${vendor.requested_units} units accepted`} />
      <Metric label="Fill rate" entry={{ value: vendor.fill_rate_pct }} kind="percent" footnote={`${vendor.received_units} of ${vendor.accepted_units} units received`} />
      <Metric label="Cancelled units" entry={{ value: vendor.cancelled_units }} kind="number" />
    </div>
  );
};

const MoverList = ({ rows, direction }) => {
  if (!rows?.length) return <Empty>No big {direction === 'up' ? 'gains' : 'drops'} this week.</Empty>;
  return (
    <ul className="divide-y divide-gray-100" data-testid={`review-movers-${direction}`}>
      {rows.map(m => (
        <li key={`${m.sku_code}-${m.platform}`} className="py-2 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <span className="min-w-0">
              <Link to={`/sales/skus?sku=${encodeURIComponent(m.sku_code)}`} className="font-mono text-xs font-medium text-blue-700 hover:underline">{m.sku_code}</Link>
              <span className="ml-2 text-gray-900">{m.product_name}</span>
              <span className="ml-2 text-xs text-gray-500">{m.platform_label}</span>
            </span>
            <span className={`font-semibold tabular-nums ${m.delta >= 0 ? 'text-green-700' : 'text-red-700'}`}>{m.delta >= 0 ? '+' : '−'}{shortMoney(Math.abs(m.delta))}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
            {m.tags.map(tag => <TagChip key={tag} tag={tag} />)}
            <span className="tabular-nums">{shortMoney(m.previous_revenue)} → {shortMoney(m.revenue)}</span>
            <span className="tabular-nums">· {m.previous_units} → {m.units} units</span>
            {m.asp != null && m.previous_asp != null && <span className="tabular-nums">· ASP {shortMoney(m.previous_asp)} → {shortMoney(m.asp)}</span>}
            {m.in_stock_days != null && <span className="tabular-nums">· in stock {m.in_stock_days}/7 days{m.stock_basis && m.stock_basis !== 'exact' ? ` (${m.stock_basis})` : ''}</span>}
            {m.ad_spend > 0 && <span className="tabular-nums">· ads {shortMoney(m.ad_spend)}</span>}
          </div>
        </li>
      ))}
    </ul>
  );
};

export const WeeklyReviewTab = ({ data, showCosts, onScope }) => {
  const [dim, setDim] = useState('by_platform');
  if (!data) return null;
  const t = data.total || {};
  return (
    <div className="space-y-4" data-testid="review-weekly-tab">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="review-kpis">
        <Metric label="Revenue" entry={t.revenue} />
        <Metric label="Units" entry={t.units} kind="number" />
        <Metric label="Average selling price" entry={t.asp} />
        <Metric label="SKUs sold" entry={t.skus_sold} kind="number" />
        <Metric label="Discount vs list" entry={t.discount_pct} kind="percent" invert points />
        <Metric label="Return rate" entry={t.return_rate_pct} kind="percent" invert footnote={t.return_units ? `${t.return_units.value ?? 0} units returned (last week ${t.return_units.previous ?? 0})` : undefined} />
        <Metric label="Revenue after returns" entry={t.revenue_after_returns} />
        <Metric label="Ad spend" entry={t.ad_spend} invert footnote={t.tacos_pct?.value != null ? `TACOS ${t.tacos_pct.value}% (last week ${t.tacos_pct.previous ?? '—'}%)` : 'No ad spend recorded'} />
        {t.orders && <Metric label={`Orders · ${t.orders.scope}`} entry={t.orders} kind="number" />}
        <Metric label="In stock (top sellers)" entry={{ value: t.in_stock_pct?.value }} kind="percent" footnote={t.in_stock_pct?.basis ? `Basis: ${t.in_stock_pct.basis}` : undefined} />
        {showCosts && t.contribution && <Metric label="Contribution" entry={t.contribution} footnote={t.contribution_margin_pct?.value != null ? `${t.contribution_margin_pct.value}% margin` : undefined} testId="review-contribution" />}
        {showCosts && t.net_profit && <Metric label="Net profit (after ads)" entry={t.net_profit} />}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Why revenue changed" subtitle="Price, volume and mix on SKUs sold both weeks; new and lost listings; the parts add up exactly.">
          <BridgeChart bridge={data.bridge} />
          <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1 text-xs sm:grid-cols-6">
            {[['price', 'Price'], ['volume', 'Volume'], ['mix', 'Mix'], ['new', 'New'], ['lost', 'Lost'], ['other', 'Other']].map(([key, label]) => (
              <div key={key}><dt className="text-gray-500">{label}</dt><dd className={`font-medium tabular-nums ${data.bridge?.[key] < 0 ? 'text-red-700' : 'text-gray-900'}`}>{shortMoney(data.bridge?.[key])}</dd></div>
            ))}
          </dl>
        </Section>
        <Section title="Target pace" subtitle="Month to date against the Targets page, and what the rest of the month needs.">
          <TargetBlock target={data.target} />
        </Section>
      </div>

      <Section title="Where it came from" right={<MetricToggle value={dim} onChange={setDim} options={DIMENSIONS} label="Group by" />}
        subtitle="Click a name to filter the whole review to it.">
        <DimensionTable rows={data[dim]} dim={dim} showCosts={showCosts} onScope={onScope} />
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title="Biggest gains" subtitle="SKU × platform, with the most likely cause.">
          <MoverList rows={data.movers?.up} direction="up" />
        </Section>
        <Section title="Biggest drops" subtitle="Stock-outs, price moves, ad changes and returns are checked before calling it demand.">
          <MoverList rows={data.movers?.down} direction="down" />
        </Section>
      </div>

      <Section title="Suggested actions" subtitle="Ranked by rupee impact; each says what it is based on.">
        <ActionList actions={data.actions} />
      </Section>

      <Section title="Amazon Vendor purchase orders" subtitle="POs dated in this week: what Amazon asked for, confirmed and received.">
        <VendorBlock vendor={data.vendor} />
      </Section>

      <Notes notes={data.notes} />
    </div>
  );
};

export default WeeklyReviewTab;
